<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, useTemplateRef, watch } from "vue"
import { useI18n } from "vue-i18n"
import { OptionTarifaire } from "@modules/courtage/constants"
import { PeriodItem } from "@types"
import { useVModel } from "@vueuse/core"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            actual: "Offre de référence du compteur",
            offer_index: "Offre alternative {index}",
            edit: ({ named }: { named: (key: string) => string }) =>
                named("edit") ? "Valider" : "Remplir",
            expand: ({ named }: { named: (key: string) => string }) =>
                named("expand") ? "Tout développer" : "Tout réduire",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
/**
 * Propriétés du composant d'offre du comparateur
 */
interface Props {
    /** Indique si c'est l'offre actuelle (de référence) du compteur */
    actual: boolean
    /** Index de l'offre dans la liste des alternatives */
    index?: number
    /** Option tarifaire sélectionnée */
    offer: OptionTarifaire
    /** Liste des périodes de tarification */
    periods: PeriodItem[]
    /** Nom du fournisseur d'énergie */
    supplier?: string
    /** Nom commercial de l'offre */
    name?: string
    /** Indique si l'énergie est utilisée par poste pour l'offre */
    useEnergieparposteElec: boolean
    /** Modèle de consommation par poste pour l'offre */
    energieparposteElec?: {
        base: number
        hp: number
        hc: number
        hph: number
        hch: number
        hpb: number
        hcb: number
        pte: number
    }
}

/**
 * Événements émis par le composant
 */
interface Emits {
    /** Événement de mise à jour de l'option tarifaire */
    (e: "update:offer", value: OptionTarifaire): void
    /** Événement de mise à jour du fournisseur */
    (e: "update:supplier", value: string): void
    /** Événement de mise à jour du nom de l'offre */
    (e: "update:name", value: string): void
    /** Événement de mise à jour des périodes */
    (e: "update:periods", value: PeriodItem[]): void
    /** Événement de mise à jour de l'utilisation de l'énergie par poste */
    (e: "update:useEnergieparposteElec", value: boolean): void
    /** Événement de mise à jour du modèle de consommation par poste */
    (
        e: "update:energieparposteElec",
        value: {
            base?: number
            hp?: number
            hc?: number
            hph?: number
            hch?: number
            hpb?: number
            hcb?: number
            pte?: number
        }
    ): void
    /** Événement de suppression de l'offre */
    (e: "delete:offer", index: number): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    actual: false,
    index: 1,
    supplier: "",
    name: "",
    offer: OptionTarifaire.Base,
    periods: () => [],
    useEnergieparposteElec: false,
    energieparposteElec: undefined,
})

const emit = defineEmits<Emits>()

// ================================
// LOGIQUE MÉTIER - ACCORDÉON
// ================================
/**
 * État de l'accordéon principal (panneau actif)
 */
const active = ref<string | null>(null)

/**
 * État d'expansion/réduction de tous les sous-accordéons
 */
const expand = ref<boolean>(false)

/**
 * Synchronise l'état expand avec l'état de l'accordéon
 * Quand l'accordéon s'ouvre/ferme par d'autres moyens, on met à jour expand
 */
watch(active, (newActive) => {
    if (newActive === "0" && !expand.value) {
        // L'accordéon s'est ouvert mais expand est false, on doit étendre les enfants
        expand.value = true
        infoGeneralRef.value?.expand()
        periodListRef.value?.expand()
    } else if (newActive === null && expand.value) {
        // L'accordéon s'est fermé mais expand est true, on doit réduire les enfants
        expand.value = false
        infoGeneralRef.value?.collapse()
        periodListRef.value?.collapse()
    }
})

// ================================
// RÉFÉRENCES DES COMPOSANTS ENFANTS
// ================================
/**
 * Référence au composant des informations générales
 */
const infoGeneralRef = useTemplateRef<any>("infoGeneralRef")

/**
 * Référence au composant de la liste des périodes
 */
const periodListRef = useTemplateRef<any>("periodListRef")

// ================================
// MODÈLES DE DONNÉES SYNCHRONISÉS
// ================================
/**
 * Modèle de l'offre tarifaire avec synchronisation bidirectionnelle
 */
const modelOffer = useVModel(props, "offer", emit)

/**
 * Modèle du fournisseur avec synchronisation bidirectionnelle
 */
const modelSupplier = useVModel(props, "supplier", emit)

/**
 * Modèle du nom de l'offre avec synchronisation bidirectionnelle
 */
const modelName = useVModel(props, "name", emit)

/**
 * Modèle des périodes avec synchronisation bidirectionnelle et observation profonde
 */
const modelPeriods = useVModel(props, "periods", emit, {
    deep: true,
    eventName: "update:periods",
    passive: true,
})

/**
 * Modèle de l'utilisation de l'énergie par poste avec synchronisation bidirectionnelle
 */
const modelUseEnergieparposteElec = useVModel(
    props,
    "useEnergieparposteElec",
    emit
)

/**
 * Modèle du modèle de consommation par poste avec synchronisation bidirectionnelle et observation profonde
 */
const modelEnergieparposteElec = useVModel(props, "energieparposteElec", emit, {
    deep: true,
    eventName: "update:energieparposteElec",
    passive: true,
})

// ================================
// VALIDATION DU FORMULAIRE
// ================================
/**
 * Indique si le formulaire complet est valide (infos générales + périodes)
 */
const getIsValid = computed<boolean>(() => {
    return !!infoGeneralRef.value?.isValid && !periodListRef.value?.hasError
})

/**
 * Déclenche la validation de tous les sous-formulaires
 */
function validate(): void {
    infoGeneralRef.value?.validate()
    periodListRef.value?.validate()
}

// ================================
// LOGIQUE MÉTIER - SUPPRESSION
// ================================

/**
 * Gère la suppression de l'offre alternative
 * Émet un événement vers le composant parent pour traiter la suppression
 */
function onClickDelete(): void {
    if (props.index !== undefined && !props.actual) {
        emit("delete:offer", props.index)
    }
}

/**
 * Détermine si l'offre peut être supprimée (seulement les offres alternatives)
 */
const getCanDelete = computed<boolean>(() => {
    return !props.actual && (props.index ?? 0) > 0
})

function onToggleExpand() {
    expand.value = !expand.value

    if (expand.value) {
        infoGeneralRef.value?.expand()
        periodListRef.value?.expand()
    } else {
        infoGeneralRef.value?.collapse()
        periodListRef.value?.collapse()
    }
}

// ================================
// API PUBLIQUE DU COMPOSANT
// ================================
defineExpose({
    isValid: getIsValid,
    validate,
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL - ACCORDÉON -->
    <!-- ================================ -->
    <BaseAccordion
        v-model:value="active"
        @tab-close="active = null"
        @tab-open="active = '0'"
        class="comparator-offer-item bg-white rounded-md"
    >
        <BaseAccordionPanel value="0">
            <!-- ================================ -->
            <!-- HEADER - INFORMATIONS GÉNÉRALES -->
            <!-- ================================ -->
            <BaseAccordionHeader
                class="comparator-offer-item-info-general__header flex-row-reverse! justify-end! gap-6! lg:gap-2! bg-transparent!"
            >
                <div class="flex items-center justify-between w-full flex-wrap">
                    <!-- Titre et actions de suppression -->
                    <div class="flex items-center gap-3">
                        <h3 class="font-bold">
                            {{
                                t(actual ? "actual" : "offer_index", {
                                    index: index + 1,
                                })
                            }}
                        </h3>
                        <i
                            class="pi pi-check"
                            :class="{ 'text-green-500': getIsValid }"
                        ></i>
                        <BaseButton
                            v-if="getCanDelete"
                            icon="pi pi-trash"
                            @click.prevent.stop="onClickDelete"
                            rounded
                            variant="link"
                        />
                    </div>
                    <div class="flex gap-2 items-center">
                        <!-- Bouton d'édition -->
                        <BaseButton
                            class="md:min-w-40"
                            :label="t('edit', { edit: active === '0' })"
                            :icon="`pi pi-${active === '0' ? 'check' : 'pencil'}`"
                            icon-pos="right"
                            @click="onToggleExpand()"
                        />
                    </div>
                </div>
            </BaseAccordionHeader>

            <!-- ================================ -->
            <!-- CONTENU PRINCIPAL -->
            <!-- ================================ -->
            <BaseAccordionContent :pt="{ content: 'rounded-b-md' }">
                <div class="flex flex-col gap-4">
                    <!-- ================================ -->
                    <!-- SECTION - INFORMATIONS GÉNÉRALES -->
                    <!-- ================================ -->
                    <ComparatorOfferItemInfoGeneral
                        ref="infoGeneralRef"
                        :actual="actual"
                        v-model:offer="modelOffer"
                        v-model:supplier="modelSupplier"
                        v-model:name="modelName"
                        v-model:use-energieparposte-elec="
                            modelUseEnergieparposteElec
                        "
                        v-model:energieparposte-elec="modelEnergieparposteElec"
                    />

                    <!-- ================================ -->
                    <!-- SECTION - PÉRIODES ET TARIFS -->
                    <!-- ================================ -->
                    <ComparatorOfferItemPeriodList
                        ref="periodListRef"
                        :actual="actual"
                        :offer="modelOffer"
                        v-model:periods="modelPeriods"
                        :energieparposte-elec="modelEnergieparposteElec"
                    />
                </div>
            </BaseAccordionContent>
        </BaseAccordionPanel>
    </BaseAccordion>
</template>
